import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  type OnInit,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'wl-loading-screen',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './loading-screen.component.html',
  styleUrls: [
    './loading-screen.component.ed.scss',
    './loading-screen.component.essent.scss',
  ],
})
export class LoadingScreenComponent implements OnInit {
  private readonly destroyRef$ = inject(DestroyRef);

  // Inputs
  public readonly title = input<string>();
  public readonly subtitle = input<string>();
  public readonly items = input<{ icon: string; text: string }[]>([]);
  public readonly duration = input<number>(3000);

  // Outputs
  public readonly finished = output<void>();

  // State
  public readonly step = signal(0);

  // Internal
  private readonly maxSteps = computed(() => this.items().length);
  private readonly isLastStep = computed(() => this.step() >= this.maxSteps());
  private readonly finishedEvent$ = new Subject<void>();
  private readonly durationPerStep = computed(
    () => this.duration() / this.maxSteps()
  );

  ngOnInit(): void {
    // Reset steps
    this.step.set(1);

    // Make sure to output when finished
    this.finishedEvent$
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe(() => this.finished.emit());

    // Increase step per interval until finished
    interval(this.durationPerStep())
      .pipe(
        takeUntilDestroyed(this.destroyRef$),
        takeUntil(this.finishedEvent$)
      )
      .subscribe(() => {
        this.nextStep();
      });
  }

  /**
   * Increase steps until max steps is reached, then emit finishedEvent$.
   */
  nextStep() {
    if (this.isLastStep()) {
      this.finishedEvent$.next();
    } else {
      this.step.update((step) => step + 1);
    }
  }
}
